import { useQueryClient } from "@tanstack/react-query";
import { getDefaultStore, useSetAtom } from "jotai";
import { useEffect, useState } from "react";

import { accessTokenAtom, userDataAtom } from "../atoms/auth-atom";
import type { ProfileDataResponseDto } from "../dtos/get-profile-data-response-dto";
import { AuthService } from "../services/auth-service";
import { ProfileService } from "../services/profile-service";
import type { AuthUser } from "../types";

const defaultStore = getDefaultStore();

const refreshAccessToken = (): Promise<string | null> => {
  return AuthService.refreshToken()
    .then((res) => res?.data?.access_token ?? null)
    .catch(() => null);
};

const mapProfileToAuthUser = (profile: ProfileDataResponseDto): AuthUser => ({
  id: profile.id,
  name: `${profile.first_name} ${profile.last_name}`.trim(),
  email: profile.email,
  roles: profile.roles,
  seller_type: profile.seller_type
});

const runBootstrap = async (queryClient: QueryClient): Promise<void> => {
  if (defaultStore.get(accessTokenAtom)) {
    return;
  }

  const token = await refreshAccessToken();

  if (!token) {
    return;
  }

  defaultStore.set(accessTokenAtom, token);

  try {
    const profile = await queryClient.fetchQuery({
      queryKey: ["profile"],
      queryFn: () => ProfileService.getProfileData()
    });

    if (profile?.data) {
      defaultStore.set(userDataAtom, mapProfileToAuthUser(profile.data));
    } else {
      defaultStore.set(accessTokenAtom, null);
    }
  } catch {
    defaultStore.set(accessTokenAtom, null);
  }
};

type QueryClient = ReturnType<typeof useQueryClient>;

let bootstrapPromise: Promise<void> | null = null;

/**
 * Restores the session on app load without persisting the access token.
 * The refresh token lives in an HttpOnly cookie, so a fresh access token is
 * requested once on boot and kept in memory only.
 */
export const useAuthBootstrap = () => {
  const queryClient = useQueryClient();
  const setAccessToken = useSetAtom(accessTokenAtom);
  const setUserData = useSetAtom(userDataAtom);
  const [isBooting, setIsBooting] = useState(true);

  useEffect(() => {
    let active = true;

    bootstrapPromise ??= runBootstrap(queryClient).catch(() => {
      setAccessToken(null);
      setUserData(null);
    });

    bootstrapPromise.finally(() => {
      if (active) {
        setIsBooting(false);
      }
    });

    return () => {
      active = false;
    };
  }, [queryClient, setAccessToken, setUserData]);

  return { isBooting };
};
